
import React from 'react';
import { motion } from 'framer-motion';
import { Brain, Heart, Users, BookOpen, CheckCircle, ArrowRightCircle, Star, Award, Lightbulb } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';

const WhyItWorksPage = () => {
  const fadeIn = (delay = 0, y = 20) => ({
    hidden: { opacity: 0, y: y, scale: 0.98 },
    visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.7, delay, ease: [0.6, -0.05, 0.01, 0.99] } },
  });

  const scientificBases = [
    {
      title: "CASEL Framework",
      description: "Classbuddy volgt de vijf kerncompetenties van sociaal-emotioneel leren: zelfbewustzijn, zelfmanagement, sociale bewustzijn, relatievaardigheden en verantwoorde besluitvorming.",
      icon: <Brain size={36} className="text-cm-blue" />
    },
    {
      title: "Executieve Functies",
      description: "Onze buddy's helpen kinderen bij het ontwikkelen van werkgeheugen, flexibel denken en impulscontrole - essentiële vaardigheden voor schoolsucces.",
      icon: <Lightbulb size={36} className="text-cm-yellow" />
    },
    {
      title: "Kindgerichte AI",
      description: "Speciaal ontworpen conversatie-AI die aansluit bij de belevingswereld van kinderen en hun ontwikkelingsfase respecteert.",
      icon: <Heart size={36} className="text-cm-pink" />
    }
  ];

  const testimonials = [
    {
      quote: "Sinds we Classbuddy gebruiken, praten kinderen veel opener over hun gevoelens. We kunnen eerder signaleren en helpen.",
      author: "Maria van der Berg",
      role: "Intern begeleider, Basisschool De Regenboog",
      rating: 5
    },
    {
      quote: "De buddy's maken het laagdrempelig voor kinderen om hulp te zoeken. Ze voelen zich veilig en begrepen.",
      author: "Tom Jansen",
      role: "Groepsleerkracht groep 6, Het Kompas",
      rating: 5
    },
    {
      quote: "Als zorgcoördinator krijg ik waardevolle inzichten zonder de privacy van kinderen te schenden. Perfect systeem.",
      author: "Linda Vermeulen",
      role: "Zorgcoördinator, Basisschool Nieuw Beginnen",
      rating: 5
    }
  ];

  const reasons = [
    {
      title: "Geen oordeel",
      description: "AI-buddy's oordelen nooit en zijn altijd geduldig. Kinderen voelen zich veilig om alles te delen.",
      icon: <CheckCircle size={32} className="text-cm-green" />
    },
    {
      title: "Altijd beschikbaar",
      description: "24/7 toegankelijk, ook buiten schooltijd. Kinderen kunnen praten wanneer zij daar behoefte aan hebben.",
      icon: <Users size={32} className="text-cm-blue" />
    },
    {
      title: "Op hun niveau",
      description: "Communicatie is afgestemd op de leeftijd en ontwikkeling van het kind. Geen ingewikkelde volwassentaal.",
      icon: <Heart size={32} className="text-cm-pink" />
    }
  ];

  return (
    <div className="container mx-auto px-4 section-padding overflow-hidden">
      <motion.div
        initial="hidden"
        animate="visible"
        className="text-center mb-16"
      >
        <motion.div variants={fadeIn(0,30)} className="flex justify-center mb-8">
          <Brain size={72} className="text-cm-blue" />
        </motion.div>
        <motion.h1
          variants={fadeIn(0.1, 30)}
          className="page-title"
        >
          Waarom werkt <span className="text-gradient-pink-blue font-medium">Classbuddy</span> zo goed?
        </motion.h1>
        <motion.p
          variants={fadeIn(0.2, 20)}
          className="page-subtitle"
        >
          Ontdek de wetenschappelijke basis en praktische ervaring achter ons platform. Van pedagogische theorie tot echte resultaten in de klas.
        </motion.p>
      </motion.div>

      {/* Scientific Foundation */}
      <motion.div variants={fadeIn(0.3, 20)} initial="hidden" animate="visible" className="mb-20">
        <h2 className="text-4xl font-sans font-medium text-cm-blue-dark mb-12 text-center">
          Wetenschappelijke <span className="text-gradient-pink-blue">Onderbouwing</span>
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {scientificBases.map((base, index) => (
            <motion.div
              key={index}
              variants={fadeIn(0.4 + index * 0.1, 20)}
              initial="hidden"
              animate="visible"
              className="glassmorphism-card p-8 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 text-center"
              whileHover={{ y: -8, scale: 1.02 }}
            >
              <div className="flex justify-center items-center mb-6 w-20 h-20 bg-gradient-to-br from-cm-blue-light to-white rounded-full shadow-md">
                {base.icon}
              </div>
              <h3 className="text-2xl font-sans font-medium text-cm-blue-dark mb-4">{base.title}</h3>
              <p className="text-cm-gray leading-relaxed">{base.description}</p>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Why Children Talk to Buddies */}
      <motion.div variants={fadeIn(0.7, 20)} initial="hidden" animate="visible" className="mb-20">
        <h2 className="text-4xl font-sans font-medium text-cm-blue-dark mb-12 text-center">
          Waarom praten kinderen <span className="text-gradient-pink-blue">makkelijker</span> tegen een buddy?
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {reasons.map((reason, index) => (
            <motion.div
              key={index}
              variants={fadeIn(0.8 + index * 0.1, 20)}
              initial="hidden"
              animate="visible"
              className="glassmorphism-card p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-start space-x-4"
              whileHover={{ y: -5, scale: 1.02 }}
            >
              <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-cm-blue-light to-white rounded-full flex items-center justify-center shadow-md">
                {reason.icon}
              </div>
              <div>
                <h3 className="text-xl font-sans font-medium text-cm-blue-dark mb-2">{reason.title}</h3>
                <p className="text-cm-gray text-sm leading-relaxed">{reason.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Testimonials */}
      <motion.div variants={fadeIn(1.1, 20)} initial="hidden" animate="visible" className="mb-20">
        <h2 className="text-4xl font-sans font-medium text-cm-blue-dark mb-12 text-center">
          Ervaring uit de <span className="text-gradient-pink-blue">Praktijk</span>
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={index}
              variants={fadeIn(1.2 + index * 0.1, 20)}
              initial="hidden"
              animate="visible"
              className="glassmorphism-card p-8 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300"
              whileHover={{ y: -8, scale: 1.02 }}
            >
              <div className="flex mb-4">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star key={i} size={20} className="text-cm-yellow fill-current" />
                ))}
              </div>
              <blockquote className="text-cm-gray italic mb-6 leading-relaxed">
                "{testimonial.quote}"
              </blockquote>
              <div>
                <div className="font-medium text-cm-blue-dark">{testimonial.author}</div>
                <div className="text-sm text-cm-gray">{testimonial.role}</div>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* CTA Section */}
      <motion.div
        variants={fadeIn(1.5, 20)}
        initial="hidden"
        animate="visible"
        className="p-10 bg-gradient-to-r from-cm-green-light/70 via-cm-yellow-light/70 to-cm-pink-light/70 rounded-3xl shadow-2xl text-center"
      >
        <Award size={56} className="text-cm-blue mx-auto mb-6" />
        <h2 className="text-4xl font-sans font-medium text-cm-blue-dark mb-5">Ervaar het zelf</h2>
        <p className="text-lg text-cm-gray mb-8 max-w-2xl mx-auto leading-relaxed">
          Theorie is mooi, maar praktijk is overtuigender. Vraag een gratis pilot aan en ontdek hoe Classbuddy het verschil maakt op uw school.
        </p>
        <Button asChild size="lg" className="bg-gradient-to-r from-cm-pink to-cm-blue hover:from-cm-pink-dark hover:to-cm-blue-dark text-white font-medium text-lg px-10 py-7 rounded-xl shadow-xl transform hover:scale-105 transition-all duration-300 group">
          <Link to="/contact?subject=PilotAanvraag">
            Vraag gratis pilot aan <ArrowRightCircle className="ml-2.5 h-5 w-5 group-hover:translate-x-1 transition-transform" />
          </Link>
        </Button>
      </motion.div>
    </div>
  );
};

export default WhyItWorksPage;
